import { useEffect } from 'react';
import { DEFAULT_OG_IMAGE, absoluteUrl, isPublicPath } from '@/lib/seo';

interface PageMetaOptions {
  title?: string;
  description?: string;
  fullTitle?: boolean;
  /** Absolute or relative image URL for og:image / twitter:image. */
  image?: string;
  /** og:type override (defaults to "website"). */
  ogType?: string;
  /** JSON-LD object(s) to inject for this page. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const MANAGED = 'data-page-meta';

/** Create/update a managed tag in <head>, keyed by selector. */
const setTag = (
  selector: string,
  create: () => HTMLElement,
  apply: (el: HTMLElement) => void,
) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    el.setAttribute(MANAGED, 'true');
    document.head.appendChild(el);
  }
  apply(el);
  return el;
};

const setMetaName = (name: string, content: string) =>
  setTag(
    `meta[name="${name}"]`,
    () => {
      const m = document.createElement('meta');
      m.setAttribute('name', name);
      return m;
    },
    (el) => el.setAttribute('content', content),
  );

const setMetaProp = (property: string, content: string) =>
  setTag(
    `meta[property="${property}"]`,
    () => {
      const m = document.createElement('meta');
      m.setAttribute('property', property);
      return m;
    },
    (el) => el.setAttribute('content', content),
  );

export const usePageMeta = ({
  title,
  description,
  fullTitle,
  image,
  ogType = 'website',
  jsonLd,
}: PageMetaOptions) => {
  useEffect(() => {
    const resolvedTitle = title
      ? fullTitle
        ? title
        : `${title} - LANERUNNER`
      : document.title;

    if (title) document.title = resolvedTitle;
    if (description) setMetaName('description', description);

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    const publicPage = isPublicPath(pathname);

    // Authenticated / non-marketing routes: never indexable, no social tags.
    if (!publicPage) {
      setMetaName('robots', 'noindex, nofollow');
      return;
    }

    // Public marketing page → self-referencing canonical + og/twitter set.
    const url = absoluteUrl(pathname);
    const img = image
      ? image.startsWith('http')
        ? image
        : `${absoluteUrl('/')}${image.replace(/^\//, '')}`
      : DEFAULT_OG_IMAGE;

    document.head
      .querySelectorAll('meta[name="robots"]')
      .forEach((el) => el.parentElement?.removeChild(el));

    setTag(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      },
      (el) => el.setAttribute('href', url),
    );

    setMetaProp('og:title', resolvedTitle);
    setMetaProp('og:url', url);
    setMetaProp('og:type', ogType);
    setMetaProp('og:site_name', 'LaneRunner');
    setMetaProp('og:image', img);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', resolvedTitle);
    setMetaName('twitter:image', img);
    if (description) {
      setMetaProp('og:description', description);
      setMetaName('twitter:description', description);
    }

    // JSON-LD (replaced on every route change)
    document.head
      .querySelectorAll(`script[type="application/ld+json"][${MANAGED}]`)
      .forEach((el) => el.parentElement?.removeChild(el));

    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute(MANAGED, 'true');
        s.text = JSON.stringify(block);
        document.head.appendChild(s);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, fullTitle, image, ogType, JSON.stringify(jsonLd ?? null)]);
};
